/**
 * Artist Profile — Phase C UI.
 *
 * The user-facing surface of the Artist system: role/badge, eligibility progress,
 * 100-day progress, Native Creative Capacity, honest NeoArtist state, Collection
 * foundation (CREATED BY / OWNED empty states) and profile editing.
 *
 * ALL status/eligibility/CC/badge values are backend-authoritative. The frontend
 * only derives harmless display percentages from those authoritative integers.
 * No Artwork data is fabricated — Collection is empty until Artwork Freeze (#14).
 */
import { useCallback, useEffect, useState } from "react";
import {
  artistApi,
  ArtistApiError,
  type StatusDetail,
  type EligibilityView,
  type CapacityView,
  type QuoteView,
  type SelfProfile,
} from "../../lib/artist-api";

import { artworkApi, type Collection } from "../../lib/artwork-api";
import { CollectionGrid } from "../artwork/CollectionCards";
import { useModerationNotifications } from "../useModerationNotifications";
import { OwnerUtilitySections } from "./OwnerUtilitySections";
import { CockpitSummary } from "../profile/CockpitSummary";
import { RewardsPanel } from "../profile/RewardsPanel";
import { MyGridPanel } from "../profile/MyGridPanel";
import { ActivityPanel } from "../profile/ActivityPanel";
import { AnalyticsPanel } from "../profile/AnalyticsPanel";
import { OnboardingProfileEntry } from "../onboarding/OnboardingProfileEntry";
import { useI18n } from "../../i18n";

type CockpitTab = "overview" | "rewards" | "activity" | "analytics" | "grid";

function cockpitTabFromHash(): CockpitTab {
  const h = typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
  if (h.startsWith("profile/")) {
    const sub = h.slice("profile/".length).split(/[?#]/)[0].trim();
    if (sub === "rewards" || sub === "activity" || sub === "analytics" || sub === "grid") return sub;
  }
  return "overview";
}

type Tab = "created" | "owned";

const TIER_LABEL: Record<string, string> = {
  GRID_ZERO: "Grid Zero",
  VIP_CORE: "VIP Core",
  PREMIUM: "Premium",
  RESERVE: "Reserve",
  STANDARD: "Standard",
};

function pct(units: number, denom: number): number {
  if (denom <= 0) return 0;
  const v = Math.floor((units * 1000) / denom) / 10; // 0.1% precision, display only
  return Math.max(0, Math.min(100, v));
}

function shortWallet(w: string): string {
  return w && w.length > 10 ? `${w.slice(0, 6)}…${w.slice(-4)}` : w;
}

function Bar({ value, tint }: { value: number; tint: string }) {
  return (
    <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
      <div className={`h-full rounded-full ${tint}`} style={{ width: `${value}%` }} />
    </div>
  );
}

function Card({ title, testId, children }: { title: string; testId?: string; children: React.ReactNode }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur"
    >
      <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">{title}</h2>
      {children}
    </section>
  );
}

function StatusChip({ status, badge }: { status: string; badge: string | null }) {
  const { t } = useI18n();
  const permanent = badge != null; // NEOARTIST / ARTIST
  const style = permanent
    ? "border-teal-300/40 bg-teal-400/10 text-teal-200"
    : status === "CREATOR"
      ? "border-amber-300/40 bg-amber-400/10 text-amber-200"
      : "border-white/20 bg-white/5 text-white/70";
  return (
    <span
      data-testid="profile-status-chip"
      data-status={status}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.16em] ${style}`}
    >
      {permanent && <span aria-hidden>★</span>}
      {status}
      {!permanent && status === "CREATOR" && <span className="opacity-60">{t("artist.profile.statusCurrentState")}</span>}
    </span>
  );
}

export function ArtistProfileRoute() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const { ownerUnread } = useModerationNotifications();
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusDetail | null>(null);
  const [elig, setElig] = useState<EligibilityView | null>(null);
  const [cap, setCap] = useState<CapacityView | null>(null);
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [profile, setProfile] = useState<SelfProfile | null>(null);
  const [coll, setColl] = useState<Collection | null>(null);
  const [tab, setTab] = useState<Tab>("created");
  const [cockpitTab, setCockpitTab] = useState<CockpitTab>(cockpitTabFromHash);

  useEffect(() => {
    const sync = () => {
      const h = window.location.hash.replace(/^#/, "");
      if (h === "profile" || h.startsWith("profile/")) setCockpitTab(cockpitTabFromHash());
    };
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setAuthError(false);
    try {
      const [s, e, c, q, p, col] = await Promise.all([
        artistApi.selfStatus(),
        artistApi.selfEligibility(),
        artistApi.selfCapacity(),
        artistApi.selfQuote(),
        artistApi.selfProfile(),
        artworkApi.selfCollection().catch(() => null),
      ]);
      setStatus(s); setElig(e); setCap(c); setQuote(q); setProfile(p); setColl(col);
    } catch (e) {
      if (e instanceof ArtistApiError && e.status === 401) setAuthError(true);
      else setErr(e instanceof Error ? e.message : t("artist.profile.loadFail"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return <div data-testid="profile-loading" className="p-10 text-center text-white/50 font-mono text-sm">{t("artist.profile.loading")}</div>;
  }

  if (authError) {
    return (
      <div data-testid="profile-auth-required" className="mx-auto max-w-md p-10 text-center">
        <h1 className="mb-3 text-xl font-semibold text-white">{t("artist.profile.connectWallet")}</h1>
        <p className="mb-6 text-white/60 text-sm">{t("artist.profile.connectBody")}</p>
        <a href="#auth" data-testid="profile-connect-link" className="inline-flex items-center gap-2 rounded-full border border-teal-300/40 bg-teal-400/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-200 no-underline">
          {t("artist.profile.goToSignIn")}
        </a>
      </div>
    );
  }

  if (err || !status || !elig || !cap || !quote || !profile) {
    return (
      <div data-testid="profile-error" className="mx-auto max-w-md p-10 text-center text-red-300">
        <p className="mb-4">{err || t("artist.profile.unavailable")}</p>
        <button onClick={() => void load()} className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80">{t("artist.profile.retry")}</button>
      </div>
    );
  }

  const acq = status.first_acquisition;
  const neoPct = pct(elig.eligibility_units, elig.neoartist_threshold_units);
  const artistPct = pct(elig.eligibility_units, elig.artist_threshold_units);
  const daysMet = acq?.timestamp_status === "RESOLVED" && (acq?.days_since_first_acquisition ?? 0) >= (acq?.artist_min_days ?? 100);
  const tsPending = !acq || acq.timestamp == null || acq.timestamp_status !== "RESOLVED";
  const tenureBlk = acq?.block;
  const tenureBlockStr = tenureBlk ? t("artist.profile.tenureBlock", { block: tenureBlk as number }) : "";

  return (
    <div data-testid="artist-profile-route" className="mx-auto w-full max-w-4xl px-4 py-8 space-y-6">
      {/* Header */}
      <header className="flex items-center gap-4">
        {ownerUnread && (
          <a
            href="#dashboard"
            data-testid="profile-moderation-alert"
            aria-label={t("artist.profile.moderationAria")}
            className="group relative inline-flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 no-underline transition-colors"
            style={{
              borderColor: "var(--neon-coral-ring)",
              background: "var(--neon-coral-soft)",
              boxShadow: "0 0 22px -8px var(--neon-coral-glow)",
            }}
          >
            <span className="notif-glow-dot h-2.5 w-2.5 shrink-0" aria-hidden />
            <span className="flex flex-col leading-tight">
              <span className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-neon-coral/80">{t("artist.profile.moderationLabel")}</span>
              <span className="font-display text-[12px] font-semibold text-neon-coral">{t("artist.profile.moderationMsg")}</span>
            </span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden className="text-neon-coral transition-transform group-hover:translate-x-0.5">
              <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center">
          {profile.profile.avatar_url ? (
            <img src={profile.profile.avatar_url} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <span className="font-mono text-white/40 text-lg">{(profile.profile.display_name || status.wallet).slice(0, 1).toUpperCase()}</span>
          )}
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-white" data-testid="profile-display-name">
            {profile.profile.display_name || shortWallet(status.wallet)}
          </h1>
          <div className="mt-1"><StatusChip status={status.status} badge={profile.badge} /></div>
          {profile.profile.bio && (
            <p
              data-testid="profile-bio"
              className="mt-2 max-w-prose whitespace-pre-wrap break-words text-sm leading-relaxed text-white/70"
            >
              {profile.profile.bio}
            </p>
          )}
        </div>
      </header>

      {/* Macro #27 — guided onboarding entry point (reuses the shared provider). */}
      <OnboardingProfileEntry />

      {/* Macro #23 — Profile as user cockpit: Overview · Rewards · My Grid.
          Overview is default so existing Artist/Utility testids stay mounted.
          Architecture is ready for a future #24 Activity tab without redesign. */}
      <nav data-testid="profile-cockpit-tabs" className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
        {([
          ["overview", t("artist.profile.tabs.overview")],
          ["rewards", t("artist.profile.tabs.rewards")],
          ["activity", t("artist.profile.tabs.activity")],
          ["analytics", t("artist.profile.tabs.analytics")],
          ["grid", t("artist.profile.tabs.grid")],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            data-testid={`profile-tab-${id}`}
            data-active={cockpitTab === id}
            onClick={() => setCockpitTab(id)}
            className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${cockpitTab === id ? "bg-white/10 text-white" : "text-white/50 hover:text-white/75"}`}
          >
            {label}
          </button>
        ))}
      </nav>

      {cockpitTab === "overview" && (
        <>
          <CockpitSummary />

          {/* Role explanation */}
      <Card title={t("artist.profile.roleTitle")} testId="profile-role-card">
        <p className="text-sm text-white/70">
          {status.status === "ARTIST" && t("artist.profile.roleArtist")}
          {status.status === "NEOARTIST" && t("artist.profile.roleNeo")}
          {status.status === "CREATOR" && t("artist.profile.roleCreator")}
          {status.status === "USER" && t("artist.profile.roleUser")}
        </p>
      </Card>

      {/* Macro #22 — Parcel Utility / Referral / Neighborhood (owner sections) */}
      <OwnerUtilitySections />


      {/* Eligibility */}
      <Card title={t("artist.profile.eligTitle")} testId="profile-eligibility">
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex justify-between font-mono text-[11px] text-white/60">
              <span>{t("artist.profile.neoThreshold")}</span>
              <span data-testid="elig-neo-pct">{neoPct}%</span>
            </div>
            <Bar value={neoPct} tint="bg-teal-400/70" />
          </div>
          <div>
            <div className="mb-1 flex justify-between font-mono text-[11px] text-white/60">
              <span>{t("artist.profile.artistThreshold")}</span>
              <span data-testid="elig-artist-pct">{artistPct}%</span>
            </div>
            <Bar value={artistPct} tint="bg-teal-400/70" />
          </div>
          <div className="text-xs text-white/50" data-testid="elig-units">
            {t("artist.profile.eligUnits", { units: elig.eligibility_units, total: elig.artist_threshold_units })}
          </div>
          {Object.keys(elig.by_tier).length > 0 && (
            <div className="grid grid-cols-2 gap-2 pt-2 sm:grid-cols-3">
              {Object.entries(elig.by_tier).map(([tier, td]) => (
                <div key={tier} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                  <div className="font-mono text-[10px] uppercase tracking-wide text-white/50">{TIER_LABEL[tier] || tier}</div>
                  <div className="text-sm text-white/80">{t("artist.profile.tierSlots", { slots: td.slots, units: td.units })}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* 100-day */}
      <Card title={t("artist.profile.tenureTitle")} testId="profile-hundred-day">
        {tsPending ? (
          <p className="text-sm text-white/60" data-testid="hundred-day-pending">
            {t("artist.profile.tenurePending", { block: tenureBlockStr })}
          </p>
        ) : (
          <div className="space-y-2 text-sm text-white/70" data-testid="hundred-day-resolved">
            <div>{t("artist.profile.firstAcq", { date: new Date((acq!.timestamp as number) * 1000).toLocaleDateString() })}</div>
            <div className="flex justify-between font-mono text-[11px] text-white/60">
              <span>{t("artist.profile.daysLine", { days: acq!.days_since_first_acquisition ?? 0, min: acq!.artist_min_days })}</span>
              <span>{daysMet ? t("artist.profile.reqMet") : t("artist.profile.daysRemaining", { n: Math.max(0, (acq!.artist_min_days) - (acq!.days_since_first_acquisition ?? 0)) })}</span>
            </div>
            <Bar value={pct(acq!.days_toward_artist ?? 0, acq!.artist_min_days)} tint="bg-teal-400/70" />
          </div>
        )}
      </Card>

      {/* Creative Capacity */}
      <Card title={t("artist.profile.ccTitle")} testId="profile-capacity">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-center">
            <div className="font-mono text-[10px] uppercase text-white/50">{t("artist.profile.ccTotal")}</div>
            <div className="text-2xl font-semibold text-white" data-testid="cc-total">{cap.total_cc}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-center">
            <div className="font-mono text-[10px] uppercase text-white/50">{t("artist.profile.ccUsed")}</div>
            <div className="text-2xl font-semibold text-white" data-testid="cc-used">{cap.used_cc}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-center">
            <div className="font-mono text-[10px] uppercase text-white/50">{t("artist.profile.ccAvailable")}</div>
            <div className="text-2xl font-semibold text-white" data-testid="cc-remaining">{cap.remaining_cc}</div>
          </div>
        </div>
        <p className="mt-3 text-xs text-white/45">{t("artist.profile.ccNote")}</p>
      </Card>

      {/* NeoArtist */}
      <Card title={t("artist.profile.neoTitle")} testId="profile-neoartist">
        {status.status === "ARTIST" || status.status === "NEOARTIST" ? (
          <p className="text-sm text-white/70">{t("artist.profile.neoHavePermanent", { status: status.status })}</p>
        ) : !quote.neoartist_eligible ? (
          <p className="text-sm text-white/70" data-testid="neo-need-eligibility">
            {t("artist.profile.neoNeedElig", { pct: neoPct })}
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-white/70">{t("artist.profile.neoEligibleBody")}</p>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-sm">
              {quote.fee_configured ? (
                <div className="text-white/80" data-testid="neo-fee">{t("artist.profile.neoFee", { fee: quote.base_fee ?? "", currencies: quote.currencies.join(" / ") })}</div>
              ) : (
                <div className="text-amber-200/80" data-testid="neo-fee-not-configured">{t("artist.profile.neoFeeNotConfigured")}</div>
              )}
            </div>
            <button
              type="button"
              disabled
              data-testid="neo-activate-disabled"
              className="w-full cursor-not-allowed rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/50"
              title={quote.payment_integration_state}
            >
              {t("artist.profile.neoPaymentPending")}
            </button>
          </div>
        )}
      </Card>

      {/* Collection */}
      <Card title={t("artist.profile.collectionTitle")} testId="profile-collection">
        <div className="mb-4 flex items-center gap-2">
          <button data-testid="collection-tab-created" onClick={() => setTab("created")} className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] ${tab === "created" ? "bg-white/10 text-white" : "text-white/50"}`}>{t("artist.profile.collCreatedBy")}</button>
          <button data-testid="collection-tab-owned" onClick={() => setTab("owned")} className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] ${tab === "owned" ? "bg-white/10 text-white" : "text-white/50"}`}>{t("artist.profile.collOwned")}</button>
          <span data-testid="collection-created-count" className="ml-auto font-mono text-[10px] text-white/40">{t("artist.profile.collCreatedCount", { n: coll?.created_artwork_count ?? 0 })}</span>
        </div>
        {tab === "created" ? (
          <CollectionGrid
            items={coll?.created_by ?? []}
            gridTestId="collection-created-grid"
            emptyTestId="collection-created-empty"
            emptyText={t("artist.profile.collCreatedEmpty")}
          />
        ) : (
          <CollectionGrid
            items={coll?.owned ?? []}
            gridTestId="collection-owned-grid"
            emptyTestId="collection-owned-empty"
            emptyText={t("artist.profile.collOwnedEmpty")}
          />
        )}
      </Card>

      {/* Edit */}
      <ProfileEditForm initial={profile} onSaved={setProfile} />
        </>
      )}

      {cockpitTab === "rewards" && <RewardsPanel />}
      {cockpitTab === "activity" && <ActivityPanel />}
      {cockpitTab === "analytics" && <AnalyticsPanel />}
      {cockpitTab === "grid" && <MyGridPanel />}
    </div>
  );
}

function ProfileEditForm({ initial, onSaved }: { initial: SelfProfile; onSaved: (p: SelfProfile) => void }) {
  const { t } = useI18n();
  const [displayName, setDisplayName] = useState(initial.profile.display_name || "");
  const [bio, setBio] = useState(initial.profile.bio || "");
  const [avatar, setAvatar] = useState(initial.profile.avatar_url || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true); setMsg(null); setError(null);
    try {
      const updated = await artistApi.updateProfile({ display_name: displayName, bio, avatar_url: avatar });
      onSaved(updated);
      setMsg(t("artist.profile.editSaved"));
    } catch (e) {
      setError(e instanceof Error ? e.message : t("artist.profile.editSaveFail"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={t("artist.profile.editTitle")} testId="profile-edit">
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">{t("artist.profile.editDisplayName")}</span>
          <input data-testid="edit-display-name" value={displayName} maxLength={40} onChange={(e) => setDisplayName(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white outline-none focus:border-teal-300/40" />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">{t("artist.profile.editBio")}</span>
          <textarea data-testid="edit-bio" value={bio} maxLength={500} rows={3} onChange={(e) => setBio(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white outline-none focus:border-teal-300/40" />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">{t("artist.profile.editAvatar")}</span>
          <input data-testid="edit-avatar" value={avatar} onChange={(e) => setAvatar(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white outline-none focus:border-teal-300/40" />
        </label>
        <div className="flex items-center gap-3">
          <button data-testid="edit-save" onClick={() => void save()} disabled={saving} className="rounded-full border border-teal-300/40 bg-teal-400/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-200 disabled:opacity-50">
            {saving ? t("artist.profile.editSaving") : t("artist.profile.editSave")}
          </button>
          {msg && <span data-testid="edit-saved" className="text-xs text-teal-300">{msg}</span>}
          {error && <span data-testid="edit-error" className="text-xs text-red-300">{error}</span>}
        </div>
      </div>
    </Card>
  );
}
